import type { PaymentPlan } from '@/api/types/PatientApi'
import {
	isSettled,
	needsAttention,
	nextDue,
	paidCount,
	positionLine,
	settledLine,
	today,
} from '@/components/BillingPage/Sections/instalments'
import { Row, RowGroup, type RowStatus, SeeAllRow, Tag } from '@/components/Rows'
import { SheetStack, type StackView } from '@/components/Rows/SheetStack'
import { formatCents } from '@/lib/money'
import { SheetTitle } from '@/ui/core/sheet'
import { Skeleton } from '@/ui/core/skeleton'
import { VStack } from '@/ui/core/stack'
import { format } from 'date-fns'
import { useState } from 'react'
import { View } from 'react-native'

/** One plan's instalments, each with where it stands. */
const PlanDetail = ({ plan }: { plan: PaymentPlan }) => {
	const day = today()
	return (
		<RowGroup>
			{plan.instalments.map((i, idx) => {
				const settled = isSettled(i)
				const attention = needsAttention(i, day)
				return (
					<Row
						key={`${i.name}-${idx}`}
						status={settled ? 'done' : attention ? 'alert' : 'wait'}
						title={positionLine(idx + 1, plan.instalments.length)}
						line={`${settled ? 'Paid' : 'Due'} ${format(i.dueOn, 'MMM d, yyyy')}`}
						tag={attention ? <Tag label="Overdue" tone="alert" /> : undefined}
						amount={formatCents(i.amountInCents)}
						amountTone={settled ? 'soft' : attention ? 'danger' : 'ink'}
						last={idx === plan.instalments.length - 1}
					/>
				)
			})}
		</RowGroup>
	)
}

/**
 * The plans sheet: opened on a plan it shows that plan; opened on See All it lists every plan
 * and a tap steps into one in place.
 */
const PlansSheet = ({
	view,
	sorted,
	onView,
	onClose,
}: {
	view: StackView<PaymentPlan> | null
	sorted: PaymentPlan[]
	onView: (view: StackView<PaymentPlan>) => void
	onClose: () => void
}) => {
	const push = (plan: PaymentPlan) => onView({ kind: 'item', item: plan, fromList: true })
	const pop = () => onView({ kind: 'list' })
	const live = sorted.filter((plan) => nextDue(plan))
	const finished = sorted.filter((plan) => !nextDue(plan))
	return (
		<SheetStack
			view={view}
			onClose={onClose}
			list={
				// The title is not inside a gapped stack: its own margin is the air above a
				// sheet's content, and a wrapper's gap on top of that made three sheets 20pt
				// looser than the other two.
				<View className="pt-2 pb-2">
					<SheetTitle glyph="calendar">Payment Plans</SheetTitle>
					{/* Both groups are guarded, and each list is worked out once.
					
					    "Paid off" was guarded and "In progress" was not, so a member whose only
					    plans were finished met a heading with nothing under it. */}
					<VStack gap={16}>
						{live.length > 0 && (
							<RowGroup title="In progress">
								<PlanRows plans={live} onOpen={push} />
							</RowGroup>
						)}
						{finished.length > 0 && (
							<RowGroup title="Paid off">
								<PlanRows plans={finished} onOpen={push} />
							</RowGroup>
						)}
					</VStack>
				</View>
			}
			item={(plan) => (
				<View className="pt-2 pb-2">
					<SheetTitle
						glyph="calendar"
						onBack={view?.kind === 'item' && view.fromList ? pop : undefined}
					>
						{plan.name}
					</SheetTitle>
					<PlanDetail plan={plan} />
				</View>
			)}
		/>
	)
}

const LIVE_MAX = 3

const PlanRows = ({
	plans,
	onOpen,
	more = false,
}: { plans: PaymentPlan[]; onOpen: (plan: PaymentPlan) => void; more?: boolean }) => {
	const day = today()
	return (
		<>
			{plans.map((plan, i) => {
				const next = nextDue(plan)
				const paid = paidCount(plan)
				// Where the plan stands is read off its instalments; the backend's flag can lag them.
				const status: RowStatus = plan.instalments.some((i) => needsAttention(i, day))
					? 'alert'
					: next
					  ? 'wait'
					  : plan.status === 'REFUNDED'
						  ? 'off'
						  : 'done'
				// Where a finished plan stands is read off its instalments too, for the same reason
				// the mark above is: `plan.status` printed "Behind" on a plan sitting under "Paid
				// off", because every instalment had been cancelled and the flag had not caught up.
				const line = next
					? `${paid} of ${plan.instalments.length} paid${
							status === 'alert' ? ' · needs attention' : ''
					  }`
					: settledLine(plan)
				return (
					<Row
						key={plan.id}
						status={status}
						title={plan.name}
						line={line}
						tag={
							status === 'alert' ? (
								<Tag label="Needs attention" tone="alert" />
							) : undefined
						}
						onPress={() => onOpen(plan)}
						last={i === plans.length - 1 && !more}
					/>
				)
			})}
		</>
	)
}

/**
 * The plans still running, a few on the tab and all of them a page away; each row opens its
 * instalments. Plans that are paid off sort after the live ones.
 */
export const Plans = ({
	plans,
	isLoading,
}: { plans?: PaymentPlan[]; isLoading: boolean }) => {
	const [view, setView] = useState<StackView<PaymentPlan> | null>(null)
	if (isLoading)
		return <Skeleton isLoading className="h-24 w-full rounded-note bg-blush" />
	if (!plans?.length) return null
	const sorted = [...plans].sort((a, b) => Number(!nextDue(a)) - Number(!nextDue(b)))
	// The live plans, up to three; with none live, the most recent of the rest so the section
	// is never empty. Everything else is a page away.
	const live = sorted.filter((p) => nextDue(p))
	const shown = live.length ? live.slice(0, LIVE_MAX) : sorted.slice(0, 1)
	const openPlan = (plan: PaymentPlan) =>
		setView({ kind: 'item', item: plan, fromList: false })
	return (
		<VStack gap={12}>
			<RowGroup title="Payment plans">
				<PlanRows plans={shown} onOpen={openPlan} more={sorted.length > shown.length} />
				{sorted.length > shown.length && (
					<SeeAllRow onPress={() => setView({ kind: 'list' })} />
				)}
			</RowGroup>
			<PlansSheet
				view={view}
				sorted={sorted}
				onView={setView}
				onClose={() => setView(null)}
			/>
		</VStack>
	)
}
